import { useEffect, useState } from "react";
import {
  isReplyDraftStorageKey,
  purgeAllReplyDrafts,
} from "@/features/post/stores/replyDraftStore";
import {
  isThreadCreateDraftStorageKey,
  purgeAllThreadCreateDrafts,
} from "@/features/thread/stores/threadCreateDraftStore";
import { createMediator } from "@/shared/lib/componentHierarchy";
import {
  useComponentNode,
  useHierarchyLifetime,
} from "@/shared/ui/component-hierarchy/hooks";

type DraftStorageEvent = {
  type: "root.legacy-draft-written";
  key: string | null;
  value: string | null;
};
export function useLegacyDraftCleanupPresenter(): void {
  const [mediator] = useState(() =>
    createMediator<"watching", DraftStorageEvent, "reply" | "thread">(
      "watching",
      (state, event) => {
        const effects: ("reply" | "thread")[] = [];
        if (event.value !== null) {
          if (isReplyDraftStorageKey(event.key)) effects.push("reply");
          if (isThreadCreateDraftStorageKey(event.key)) effects.push("thread");
        }
        return { status: "handled", state, effects };
      },
      {
        runEffect: (effect) => {
          if (effect === "reply") purgeAllReplyDrafts();
          else purgeAllThreadCreateDrafts();
        },
      },
    ),
  );
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) => {
    if (event.type !== "root.legacy-draft-written") return "unhandled";
    mediator.dispatch(event as DraftStorageEvent);
    return "handled";
  });
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      node.dispatch({
        type: "root.legacy-draft-written",
        key: event.key,
        value: event.newValue,
      } as DraftStorageEvent);
    };
    window.addEventListener("storage", onStorage);
    return (): void => window.removeEventListener("storage", onStorage);
  }, [node]);
}
